import { FirestoreDataConverter } from '@angular/fire/firestore';
import { Settings } from './settings.base';
import { SettingsEnum } from '../enums/settings.enum';
import { ObjectHelper } from '../helpers/object.helper';

/** GIM formats (ExternalEstimateDTO.displayArea) */
export const GIM_DISPLAY_AREAS = ['FORMAT_1', 'FORMAT_2', 'FORMAT_4', 'FORMAT_8', 'FORMAT_12', 'FORMAT_PANO',
  'FORMAT_MD', 'FORMAT_CM', 'FORMAT_ADB', 'FORMAT_FDB', 'FORMAT_1_5_CC'];
/** GIM categories (ExternalEstimateDTO.displayCategory) */
export const GIM_DISPLAY_CATEGORIES = ['CAISSON', 'COLLE'];
/** Faces of a caisson (ExternalEstimateDTO.sideType). Collé has no face. */
export const GIM_SIDE_TYPES = ['RECTO_SEUL', 'RECTO_VERSO'] as const;
/** Print types of a recto seul caisson (ExternalEstimateDTO.printType). */
export const GIM_PRINT_TYPES = ['NUMERIQUE', 'NUMERIQUE_LC_H_Z', 'NUMERIQUE_LC_C_NZ', 'NUMERIQUE_LC_C_Z',
  'NUMERIQUE_LC_P_NZ', 'NUMERIQUE_LC_PP_NZ', 'NUMERIQUE_LC_PP_Z'] as const;

/** Side types that change the Multipress product. Only a caisson has one. */
function sideTypesOf(displayCategory: string): readonly string[] {
  return displayCategory === 'CAISSON' ? GIM_SIDE_TYPES : [''];
}

/** Print types that change the Multipress product. Only a recto seul caisson has one. */
function printTypesOf(sideType: string): readonly string[] {
  return sideType === 'RECTO_SEUL' ? GIM_PRINT_TYPES : [''];
}

/**
 * Mapping already saved for this product. A missing depot, face or print type still applies,
 * so settings saved before that dimension fill every new row.
 * @param mappings The saved mappings of the same format and category
 * @param depot The depot key
 * @param sideType The face, empty for collé
 * @param printType The print type, empty except for recto seul
 * @returns The saved mapping, when one applies
 */
function findSavedMapping(mappings: Partial<GimMapping>[], depot: string, sideType: string, printType: string): Partial<GimMapping> | undefined {
  const pick = (wantedDepot: string, wantedSide: string, wantedPrint: string) => mappings.find(mapping =>
    (mapping.depot || '') === wantedDepot && (mapping.sideType || '') === wantedSide && (mapping.printType || '') === wantedPrint);
  if (sideType === 'RECTO_SEUL') {
    return pick(depot, sideType, printType)
      ?? pick(depot, sideType, '')
      ?? pick(depot, '', '')
      ?? pick('', sideType, printType)
      ?? pick('', sideType, '')
      ?? pick('', '', '');
  }
  return pick(depot, sideType, '')
    ?? pick(depot, '', '')
    ?? pick('', sideType, '')
    ?? pick('', '', '');
}
/** Multipress price grid of the JCDecaux products */
export const GIM_PRICE_LIST_ID = 99998;
/** Depot keys, the same as functions/functions/gim/helpers/gim-depot.helper.js */
export const GIM_DEPOTS = ['plaisir', 'stPriest'] as const;

/**
 * Link between a GIM product (format, category, face, print type and depot) and a Multipress product.
 * The face is only set for a caisson. The print type is only set for a recto seul caisson.
 */
export class GimMapping {
  displayArea: string;
  displayCategory: string;
  /** RECTO_SEUL or RECTO_VERSO for a caisson, empty otherwise */
  sideType: string;
  /** Print type of a recto seul caisson, empty otherwise */
  printType: string;
  /** plaisir or stPriest */
  depot: string;
  /** Code of the mp-variation, empty when not mapped */
  mpVariationCode: string;

  constructor(data?: Partial<GimMapping>) {
    this.displayArea = data?.displayArea ?? '';
    this.displayCategory = data?.displayCategory ?? '';
    this.sideType = data?.sideType ?? '';
    this.printType = data?.printType ?? '';
    this.depot = data?.depot ?? '';
    this.mpVariationCode = data?.mpVariationCode ?? '';
  }
}

export class GimSettings extends Settings {
  converter: FirestoreDataConverter<GimSettings> = {
    toFirestore: settings => {
      const obj = ObjectHelper.anonymize(settings);
      delete obj.converter;
      delete obj.type;
      delete obj.id;
      // Only keep the mapped products
      obj.mappings = obj.mappings.filter((mapping: GimMapping) => mapping.mpVariationCode);
      return obj;
    },
    fromFirestore: (snapshot, options) => new GimSettings({ ...snapshot.data(options), id: snapshot.id })
  };
  type = SettingsEnum.GIM;

  /** One entry per GIM product. A collé product has no face, a recto verso caisson has no print type. */
  mappings: GimMapping[];

  constructor(data?: Partial<GimSettings>) {
    super(data);
    const savedMappings = data?.mappings ?? [];
    this.mappings = GIM_DISPLAY_AREAS.flatMap(displayArea => GIM_DISPLAY_CATEGORIES.flatMap(displayCategory =>
      sideTypesOf(displayCategory).flatMap(sideType => printTypesOf(sideType).flatMap(printType =>
        GIM_DEPOTS.map(depot => {
          const sameProduct = savedMappings.filter(mapping => mapping.displayArea === displayArea &&
            mapping.displayCategory === displayCategory);
          const saved = findSavedMapping(sameProduct, depot, sideType, printType);
          return new GimMapping({ ...saved, displayArea, displayCategory, sideType, printType, depot });
        })))));
  }
}
